/**
 * ScaleGizmo — SketchUp-style scale handles on the selected Scale feature.
 *
 * A wireframe bounding box with grips over the body's CURRENT extents:
 *   · 6 face handles  → stretch that axis; the OPPOSITE face stays anchored
 *   · 8 corner handles → uniform scale about the OPPOSITE corner
 *
 * Every drag composes onto the feature's per-axis (center, factors) via
 * composeAxisScale, previews live through the document preview channel with
 * a "1.00 × 1.00 × 1.45" readout, and commits one undo step on release.
 * The handles re-seat themselves on every evaluation, so they track the
 * body while it stretches.
 */

import * as THREE from 'three/webgpu';

import type { CadDocument } from '@core/document';
import { composeAxisScale } from '@core/geometry/meshUtils';
import type { FeatureId, FeatureNode, ScaleParams } from '@core/timeline/types';
import type { DragHandler } from '@render/cadControls';
import type { Viewport } from '@render/viewport';
import { ensureNormals } from '@render/meshView';
import type { DimInput } from '@ui/dimInput';

type V3 = [number, number, number];

const FACE_MATERIAL = new THREE.MeshBasicMaterial({ color: 0x4c8dd6, depthTest: false });
const CORNER_MATERIAL = new THREE.MeshBasicMaterial({ color: 0x6adf7e, depthTest: false });
const EDGE_MATERIAL_H = new THREE.MeshBasicMaterial({ color: 0xd6a45e, depthTest: false });
const FRAME_MATERIAL = new THREE.LineBasicMaterial({ color: 0x4c8dd6, depthTest: false });
const MIN_FACTOR = 0.01;

interface HandleInfo {
  kind: 'face' | 'corner' | 'edge';
  /** Faces: scaled axis. Edges: the axis the edge RUNS ALONG (not scaled). */
  axis: 0 | 1 | 2;
  side: 1 | -1;
  /** Corner/edge handles: signs per axis (0 = the edge's own axis). */
  signs: V3;
  position: THREE.Vector3;
}

export class ScaleGizmo {
  private group = new THREE.Group();
  private handles: { mesh: THREE.Mesh; info: HandleInfo }[] = [];
  private featureId: FeatureId | null = null;
  private bboxMin: V3 = [0, 0, 0];
  private bboxMax: V3 = [0, 0, 0];

  constructor(
    private readonly viewport: Viewport,
    private readonly host: { readonly doc: CadDocument },
    private readonly dim?: DimInput,
  ) {
    this.group.renderOrder = 999;
    viewport.gizmos.add(this.group);
  }

  /** Show handles when `id` is a scale feature with a live body; else hide. */
  sync(id: FeatureId | null): void {
    const doc = this.host.doc;
    const feature = id ? doc.timeline.get(id) : undefined;
    const result = id ? doc.lastReport?.results.get(id) : undefined;
    if (!feature || feature.type !== 'scale' || result?.kind !== 'body' ||
        result.mesh.positions.length === 0) {
      this.clear();
      return;
    }
    this.featureId = id;
    this.rebuild(result.mesh.positions);
  }

  private clear(): void {
    this.featureId = null;
    this.group.clear();
    this.handles = [];
    this.viewport.requestRender();
  }

  private rebuild(positions: Float32Array): void {
    this.group.clear();
    this.handles = [];

    const min: V3 = [Infinity, Infinity, Infinity];
    const max: V3 = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < positions.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        const v = positions[i + k]!;
        if (v < min[k]!) min[k] = v;
        if (v > max[k]!) max[k] = v;
      }
    }
    this.bboxMin = min;
    this.bboxMax = max;
    const mid: V3 = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    const diag = Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]) || 1;
    const h = Math.max(diag * 0.035, 1.5); // grip size

    // Wireframe box.
    const box = new THREE.Box3(new THREE.Vector3(...min), new THREE.Vector3(...max));
    const frame = new THREE.Box3Helper(box, 0x4c8dd6);
    (frame.material as THREE.LineBasicMaterial).depthTest = false;
    ensureNormals(frame.geometry as THREE.BufferGeometry);
    this.group.add(frame);

    const addHandle = (info: HandleInfo): void => {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(h, h, h),
        info.kind === 'face'
          ? FACE_MATERIAL
          : info.kind === 'corner'
            ? CORNER_MATERIAL
            : EDGE_MATERIAL_H,
      );
      mesh.position.copy(info.position);
      mesh.renderOrder = 1000;
      this.group.add(mesh);
      this.handles.push({ mesh, info });
    };

    // 6 face handles.
    for (const axis of [0, 1, 2] as const) {
      for (const side of [1, -1] as const) {
        const p: V3 = [...mid];
        p[axis] = side > 0 ? max[axis]! : min[axis]!;
        addHandle({
          kind: 'face',
          axis,
          side,
          signs: [0, 0, 0],
          position: new THREE.Vector3(...p),
        });
      }
    }
    // 8 corner handles.
    for (const sx of [1, -1] as const) {
      for (const sy of [1, -1] as const) {
        for (const sz of [1, -1] as const) {
          const p: V3 = [
            sx > 0 ? max[0] : min[0],
            sy > 0 ? max[1] : min[1],
            sz > 0 ? max[2] : min[2],
          ];
          addHandle({
            kind: 'corner',
            axis: 0,
            side: 1,
            signs: [sx, sy, sz],
            position: new THREE.Vector3(...p),
          });
        }
      }
    }
    // 12 edge handles: midpoint of each edge; scales the two axes the edge
    // does NOT run along, anchored at the opposite edge.
    for (const along of [0, 1, 2] as const) {
      const [a1, a2] = ([[1, 2], [0, 2], [0, 1]] as const)[along]!;
      for (const s1 of [1, -1] as const) {
        for (const s2 of [1, -1] as const) {
          const p: V3 = [...mid];
          p[a1] = s1 > 0 ? max[a1]! : min[a1]!;
          p[a2] = s2 > 0 ? max[a2]! : min[a2]!;
          const signs: V3 = [0, 0, 0];
          signs[a1] = s1;
          signs[a2] = s2;
          addHandle({
            kind: 'edge',
            axis: along,
            side: 1,
            signs,
            position: new THREE.Vector3(...p),
          });
        }
      }
    }
    this.viewport.requestRender();
  }

  /** CADControls.onDragStart hook — runs BEFORE body push/pull. */
  tryStart = (e: PointerEvent): DragHandler | null => {
    if (!this.featureId || this.handles.length === 0) return null;
    const ray = this.pointerRay(e);
    const hits = ray.intersectObjects(this.handles.map((hd) => hd.mesh), false);
    const hit = hits[0];
    if (!hit) return null;
    const handle = this.handles.find((hd) => hd.mesh === hit.object)!;

    const doc = this.host.doc;
    const feature = doc.timeline.get(this.featureId) as FeatureNode<'scale'> | undefined;
    if (!feature) return null;
    const params = feature.params as ScaleParams;
    const uniform = params.factor ?? 1;
    const baseFactors: V3 = [...(params.factors ?? [uniform, uniform, uniform])] as V3;
    const baseCenter: V3 = [...params.center] as V3;
    const base = doc.timeline;
    let lastPreview: Promise<unknown> = Promise.resolve();

    const apply = (compose: () => { center: V3; factors: V3; label: string }): void => {
      const { center, factors, label } = compose();
      this.dimText = label;
      lastPreview = doc.preview(
        base.updateFeature(feature.id, { ...params, center, factors }),
      );
    };
    const fmt = (f: V3): string => `${f[0].toFixed(2)} × ${f[1].toFixed(2)} × ${f[2].toFixed(2)}`;

    const mid: V3 = [
      (this.bboxMin[0]! + this.bboxMax[0]!) / 2,
      (this.bboxMin[1]! + this.bboxMax[1]!) / 2,
      (this.bboxMin[2]! + this.bboxMax[2]!) / 2,
    ];

    if (handle.info.kind === 'face') {
      const axis = handle.info.axis;
      const axisDir = new THREE.Vector3();
      axisDir.setComponent(axis, 1);
      const h0 = handle.info.position.clone();
      // ⌥/Alt (read per MOVE, so it can toggle mid-drag) scales about the
      // center, SketchUp-style; default anchors the opposite face.
      const anchorOpp = handle.info.side > 0 ? this.bboxMin[axis]! : this.bboxMax[axis]!;
      const anchorCen = mid[axis]!;

      return this.dragHandler(e, (ev) => {
        const anchor = ev.altKey ? anchorCen : anchorOpp;
        const span = h0.getComponent(axis) - anchor;
        if (Math.abs(span) < 1e-6) return;
        const d = this.axisParam(axisDir, h0, ev);
        let s = (span + d) / span;
        // Snap the RESULTING factor to 0.05 for tidy numbers.
        s = Math.max((MIN_FACTOR / Math.max(baseFactors[axis], 1e-9)),
          Math.round(baseFactors[axis] * s * 20) / 20 / Math.max(baseFactors[axis], 1e-9));
        apply(() => {
          const r = composeAxisScale(baseCenter, baseFactors, axis, s, anchor);
          return {
            center: r.center,
            factors: r.factors,
            label: fmt(r.factors) + (ev.altKey ? ' · center' : ''),
          };
        });
      }, () => `Scale ${feature.name}`, () => lastPreview);
    }

    // Corner (all 3 axes) and edge (2 axes): scale about the opposite grip,
    // or about the center while ⌥/Alt is held.
    const h0 = handle.info.position.clone();
    const scaledAxes: (0 | 1 | 2)[] =
      handle.info.kind === 'corner'
        ? [0, 1, 2]
        : ([0, 1, 2] as const).filter((k) => handle.info.signs[k] !== 0);
    const anchorOpp: V3 = [h0.x, h0.y, h0.z];
    const anchorCen: V3 = [h0.x, h0.y, h0.z];
    for (const k of scaledAxes) {
      anchorOpp[k] = handle.info.signs[k]! > 0 ? this.bboxMin[k]! : this.bboxMax[k]!;
      anchorCen[k] = mid[k]!;
    }

    return this.dragHandler(e, (ev) => {
      const anchor = ev.altKey ? anchorCen : anchorOpp;
      const anchorV = new THREE.Vector3(...anchor);
      const dir = h0.clone().sub(anchorV);
      const len = dir.length();
      if (len < 1e-6) return;
      dir.normalize();
      const d = this.axisParam(dir, h0, ev);
      let s = (len + d) / len;
      s = Math.max(0.02, Math.round(s * 40) / 40);
      apply(() => {
        let cur = { center: baseCenter, factors: baseFactors };
        for (const axis of scaledAxes) {
          if (cur.factors[axis] * s < MIN_FACTOR) continue;
          cur = composeAxisScale(cur.center, cur.factors, axis, s, anchor[axis]!);
        }
        return {
          center: cur.center,
          factors: cur.factors,
          label: fmt(cur.factors) + (ev.altKey ? ' · center' : ''),
        };
      });
    }, () => `Scale ${feature.name}`, () => lastPreview);
  };

  // --- plumbing -----------------------------------------------------------------

  private dragHandler(
    _start: PointerEvent,
    onMove: (e: PointerEvent) => void,
    label: () => string,
    settled: () => Promise<unknown>,
  ): DragHandler {
    const doc = this.host.doc;
    return {
      move: (ev: PointerEvent): void => {
        onMove(ev);
        if (this.dimText) this.dim?.show(this.dimText, ev.clientX, ev.clientY, null);
      },
      up: (): void => {
        this.dim?.hide();
        // Wait for the LAST preview to settle before committing — previews
        // coalesce and run async, so Promise.resolve() would commit before
        // the final drag frame's timeline landed, dropping it.
        void settled().then(() => doc.commitPreview(label()));
      },
    };
  }

  private dimText: string | null = null;

  private pointerRay(e: PointerEvent): THREE.Raycaster {
    const rect = this.viewport.element.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.viewport.camera);
    return ray;
  }

  /** Displacement of the pointer along `axis` through `origin` (world units). */
  private axisParam(axis: THREE.Vector3, origin: THREE.Vector3, e: PointerEvent): number {
    const ray = this.pointerRay(e);
    const a = origin.clone().addScaledVector(axis, -1e5);
    const b = origin.clone().addScaledVector(axis, 1e5);
    const onSegment = new THREE.Vector3();
    ray.ray.distanceSqToSegment(a, b, undefined, onSegment);
    return onSegment.sub(origin).dot(axis);
  }
}
